import { Table as CanvasTable } from '@design-systems/canvas-ui'
import { useMemo, useState } from 'react'
import { SortingState, Updater } from '@tanstack/react-table'
import './table-compat-styles.scss'

type LegacySortDirection = 'asc' | 'desc' | 'dsc' | undefined

type LegacyOnSort = {
    bivarianceHack(sortDirection: LegacySortDirection, field: string): void
}['bivarianceHack']

type LegacyColumnDef = {
    field?: string
    headerName?: string
    id?: string
    isRowHeader?: boolean
    resizable?: boolean
    width?: number
    pinned?: boolean
    sortable?: boolean
    sortType?: 'character' | 'numeric' | 'date' | 'boolean'
    defaultSort?: LegacySortDirection
    onSort?: LegacyOnSort
}

type LegacyTableData = {
    columnDefs?: LegacyColumnDef[]
    rowData?: Array<Record<string, unknown>>
    defaultColDef?: {
        sortable?: boolean
    }
}

type LegacyTableProps = {
    data?: LegacyTableData | null
    name?: string
    headers?: string[]
    scrollableWidth?: boolean
    alternateRowColor?: boolean
    cellPadding?: 'dense' | 'normal' | 'expanded'
    className?: string
    'data-testid'?: string
    hasGrid?: boolean
    fixedHeader?: boolean
    height?: number
    highlightOnHover?: 'none' | 'row' | 'cell'
    roundCorner?: boolean
    children?: React.ReactNode
}

const normalizeSortDirection = (
    direction: LegacySortDirection | undefined,
): 'asc' | 'desc' | null => {
    if (direction === 'asc') return 'asc'
    if (direction === 'desc' || direction === 'dsc') return 'desc'
    return null
}

const toSortingState = (columns: LegacyColumnDef[]): SortingState => {
    const defaultColumn = columns.find(
        (col) => normalizeSortDirection(col.defaultSort) !== null,
    )
    const sortDirection = normalizeSortDirection(defaultColumn?.defaultSort)

    if (!defaultColumn || !sortDirection) {
        return []
    }

    const columnId = defaultColumn.field || defaultColumn.id
    if (!columnId) {
        return []
    }

    return [
        {
            id: columnId,
            desc: sortDirection === 'desc',
        },
    ]
}

const MIN_SCREEN_WIDTH = 320
export const Table = ({
    data,
    name = 'Data table',
    headers,
    scrollableWidth,
    alternateRowColor,
    cellPadding = 'dense',
    className,
    'data-testid': dataTestId,
    hasGrid = true,
    fixedHeader,
    height,
    highlightOnHover,
    roundCorner,
    children,
}: LegacyTableProps) => {
    if (!scrollableWidth) {
        const columnWidths =
            data?.columnDefs?.reduce((result, column) => {
                return result + (column.width || 0)
            }, 0) ?? 0
        if (columnWidths > MIN_SCREEN_WIDTH) {
            // eslint-disable-next-line no-console
            console.error(
                `Table (${name}) width ${columnWidths}px exceeds minimum screen width ${MIN_SCREEN_WIDTH}px. ` +
                    `Enable scrollableWidth or adjust column widths.`,
            )
        }
    }

    const tableName = name || headers?.join(', ') || 'Data table'
    const columns = useMemo(() => data?.columnDefs || [], [data?.columnDefs])
    const hasServerSortHandlers = useMemo(
        () => columns.some((column) => Boolean(column.onSort)),
        [columns],
    )

    const mappedData = useMemo(() => {
        const defaultSortable = Boolean(data?.defaultColDef?.sortable)

        return {
            defaultColDef: {
                isSortable: defaultSortable,
            },
            columnDefs: columns
                .map((column, index) => {
                    const id = column.field || column.id || `column_${index}`

                    return {
                        id,
                        header: column.headerName || id,
                        isRowHeader: Boolean(column.isRowHeader),
                        isResizable: Boolean(column.resizable),
                        width: column.width,
                        pinned: Boolean(column.pinned),
                        isSortable: column.sortable ?? defaultSortable,
                        sortType: column.sortType,
                        // In server mode the initial sort lives in sortingState below. Passing
                        // defaultSort to Canvas makes it a permanent baseline that stays active
                        // (and keeps its indicator) when another column is sorted.
                        defaultSort: hasServerSortHandlers
                            ? null
                            : normalizeSortDirection(column.defaultSort),
                    }
                })
                .filter((column) => Boolean(column.id)),
            rowData: data?.rowData || [],
        }
    }, [
        columns,
        data?.defaultColDef?.sortable,
        data?.rowData,
        hasServerSortHandlers,
    ])

    const [sortingState, setSortingState] = useState<SortingState>(
        toSortingState(columns),
    )
    const onSortingChange = (nextSortingState: Updater<SortingState>) => {
        const resolvedSortingState =
            typeof nextSortingState === 'function'
                ? nextSortingState(sortingState)
                : nextSortingState

        const prevTopSort = sortingState[0]
        setSortingState(resolvedSortingState)

        const topSort = resolvedSortingState[0]
        if (!topSort) {
            // Sort was cleared — notify the consumer so it can reset to original order.
            // Use the previously-sorted column to identify which handler to call.
            if (prevTopSort) {
                const column = columns.find(
                    (col) => (col.field || col.id) === prevTopSort.id,
                )
                const sortHandler = column?.onSort
                if (sortHandler && (column?.field || column?.id)) {
                    sortHandler(
                        undefined,
                        column.field || column.id || prevTopSort.id,
                    )
                }
            }
            return
        }

        const column = columns.find(
            (col) => (col.field || col.id) === topSort.id,
        )
        const sortHandler = column?.onSort

        if (sortHandler && (column?.field || column?.id)) {
            sortHandler(
                topSort.desc ? 'desc' : 'asc',
                column.field || column.id || topSort.id,
            )
        }
    }

    const tableClassName = ['mpm-table-compat', className]
        .filter(Boolean)
        .join(' ')

    // Some legacy screens still render child-based <Table><tr/><td/>...</Table> markup.
    // Keep that path working while we complete full data-table migration.
    if (!data && children) {
        return (
            <div className={tableClassName} data-testid={dataTestId}>
                <table
                    className="mpm-table-compat__native"
                    aria-label={tableName}
                >
                    {children}
                </table>
            </div>
        )
    }

    return (
        <CanvasTable
            data={mappedData}
            name={tableName}
            className={tableClassName}
            data-testid={dataTestId}
            isWidthScrollable={scrollableWidth}
            alternateRowColor={alternateRowColor}
            cellPadding={cellPadding}
            hasGrid={hasGrid}
            isHeaderFixed={fixedHeader}
            height={height}
            highlightOnHover={highlightOnHover}
            isCornerRounded={roundCorner}
            mode={hasServerSortHandlers ? 'server' : 'client'}
            sorting={
                hasServerSortHandlers
                    ? {
                          sortingState,
                          onSortingChange,
                          sortOrderCycle: ['asc', 'desc', null],
                      }
                    : undefined
            }
        />
    )
}
